<!--
  区域配置页面
  功能：管理各省份（及城市例外）的筹建督导、运营督导、配送仓库及起订量配置
  说明：city=null 为省级默认行（四项字段均生效）；city 非空为城市例外行（仅覆盖营运督导）
  路由：/dudao/supervisorRegion
  权限模块：dudaoRegion
-->
<template>
  <div>
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>督导部</el-breadcrumb-item>
      <el-breadcrumb-item>区域配置</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="box-card" style="margin-top: 15px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">区域配置</span>
        <el-button style="float:right;" type="primary" size="small" @click="openAddDialog">新增配置</el-button>
        <el-upload
          style="float:right; margin-right: 10px;"
          :action="importUrl"
          :headers="uploadHeaders"
          :show-file-list="false"
          :on-success="handleImportSuccess"
          :on-error="handleImportError"
          :before-upload="beforeImportUpload"
          accept=".xlsx,.xls"
        >
          <el-button type="success" icon="el-icon-upload2" size="small">导入</el-button>
        </el-upload>
        <el-button style="float:right; margin-right: 10px;" type="warning" icon="el-icon-download" size="small" @click="handleDownloadTemplate">下载模板</el-button>
        <el-button style="float:right; margin-right: 10px;" type="info" icon="el-icon-download" size="small" @click="handleExport">导出</el-button>
        <el-button style="float:right; margin-right: 10px;" type="primary" icon="el-icon-refresh" size="small" @click="handleReassign" :loading="reassigning">同步到门店</el-button>
      </div>

      <!-- 搜索栏：省份、城市、筹建督导、营运督导、配送仓库五个维度下拉筛选 -->
      <div style="margin-bottom: 15px; display: flex; align-items: center; gap: 10px;">
        <el-select
          v-model="searchProvince"
          placeholder="省份"
          clearable
          filterable
          size="small"
          style="width: 180px;"
          @change="onSearchProvinceChange"
        >
          <el-option v-for="p in provinceList" :key="p" :label="p" :value="p"></el-option>
        </el-select>
        <el-select
          v-model="searchCity"
          placeholder="城市"
          clearable
          filterable
          size="small"
          style="width: 160px;"
          @change="loadList"
        >
          <el-option v-for="c in searchCityList" :key="c.code" :label="c.name" :value="c.name"></el-option>
        </el-select>
        <el-select
          v-model="searchConstructionSupervisor"
          placeholder="筹建督导"
          clearable
          filterable
          size="small"
          style="width: 160px;"
          @change="loadList"
        >
          <el-option v-for="e in constructionSupervisorList" :key="e.name" :label="e.name" :value="e.name"></el-option>
        </el-select>
        <el-select
          v-model="searchOperationsSupervisor"
          placeholder="营运督导"
          clearable
          filterable
          size="small"
          style="width: 160px;"
          @change="loadList"
        >
          <el-option v-for="e in operationsSupervisorList" :key="e._id" :label="formatOperationsOptionLabel(e)" :value="e.name"></el-option>
        </el-select>
        <el-select
          v-model="searchWarehouse"
          placeholder="配送仓库"
          clearable
          filterable
          size="small"
          style="width: 180px;"
          @change="loadList"
        >
          <el-option v-for="w in warehouseList" :key="w" :label="w" :value="w"></el-option>
        </el-select>
        <el-button type="primary" size="small" @click="loadList">搜索</el-button>
      </div>

      <!-- 配置列表表格 -->
      <el-table :data="list" border stripe v-loading="loading" style="width: 100%;">
        <el-table-column label="#" type="index" width="50"></el-table-column>
        <el-table-column prop="province" label="省份" width="120"></el-table-column>
        <!-- 城市列，省级默认行显示"—"，例外行显示城市名 -->
        <el-table-column label="城市" width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.city">{{ scope.row.city }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <!-- 筹建督导列，点击可快速筛选 -->
        <el-table-column label="筹建督导" width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.constructionSupervisorName" class="link-text" @click="quickFilter('construction', scope.row.constructionSupervisorName)">{{ scope.row.constructionSupervisorName }}</span>
            <span v-else class="text-muted">未配置</span>
          </template>
        </el-table-column>
        <!-- 运营督导列，点击可快速筛选 -->
        <el-table-column label="营运督导" width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.operationsSupervisorName" class="link-text" @click="quickFilter('operations', scope.row.operationsSupervisorName)">{{ scope.row.operationsSupervisorName }}</span>
            <span v-else class="text-muted">未配置</span>
          </template>
        </el-table-column>
        <!-- 配送仓库列，点击可快速筛选 -->
        <el-table-column label="配送仓库" width="160">
          <template slot-scope="scope">
            <span v-if="scope.row.warehouse" class="link-text" @click="quickFilter('warehouse', scope.row.warehouse)">{{ scope.row.warehouse }}</span>
            <span v-else class="text-muted">未配置</span>
          </template>
        </el-table-column>
        <el-table-column label="起订量" width="100">
          <template slot-scope="scope">
            <span v-if="scope.row.minOrder">{{ scope.row.minOrder }}件</span>
            <span v-else class="text-muted">未配置</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button type="danger" size="mini" @click="handleRemove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑配置弹窗 -->
    <el-dialog :close-on-click-modal="false" :title="dialogMode === 'add' ? '新增配置' : '编辑配置'" :visible.sync="dialogVisible" width="550px">
      <el-form :model="formData" label-width="100px" size="small">
        <!-- 省份选择，编辑时不可修改（与城市共同构成唯一键） -->
        <el-form-item label="省份">
          <el-select
            v-model="formData.province"
            placeholder="请选择省份"
            filterable
            :disabled="dialogMode === 'edit'"
            style="width: 100%;"
            @change="onDialogProvinceChange"
          >
            <el-option v-for="p in provinceList" :key="p" :label="p" :value="p"></el-option>
          </el-select>
        </el-form-item>
        <!-- 城市选择：留空=全省默认行；选择城市=城市例外行（仅覆盖营运督导），编辑时不可修改 -->
        <el-form-item label="城市">
          <el-select
            v-model="formData.city"
            placeholder="留空为全省默认"
            filterable
            clearable
            :disabled="dialogMode === 'edit'"
            :loading="cityLoading"
            style="width: 100%;"
            @change="onDialogCityChange"
          >
            <el-option v-for="c in dialogCityList" :key="c.code" :label="c.name" :value="c.name"></el-option>
          </el-select>
          <div v-if="formData.city" style="font-size: 12px; color: #E6A23C; line-height: 1.6;">
            城市例外行仅配置营运督导，筹建督导/配送仓库/起订量仍按省级默认行生效
          </div>
        </el-form-item>
        <!-- 筹建督导选择，只列出筹建督导角色的员工（仅省级行可配） -->
        <el-form-item v-if="!formData.city" label="筹建督导">
          <el-select
            v-model="formData.constructionSupervisorId"
            placeholder="请选择筹建督导"
            filterable
            clearable
            @change="onConstructionSupervisorChange"
            style="width: 100%;"
          >
            <el-option
              v-for="e in constructionSupervisorList"
              :key="e._id"
              :label="e.name"
              :value="e._id"
            ></el-option>
          </el-select>
        </el-form-item>
        <!-- 营运督导选择，列出营运督导岗位及筹建督导岗位（存在一人兼两岗情况）；城市例外行必选 -->
        <el-form-item label="营运督导">
          <el-select
            v-model="formData.operationsSupervisorId"
            :placeholder="formData.city ? '请选择营运督导（必选）' : '请选择营运督导'"
            filterable
            clearable
            @change="onOperationsSupervisorChange"
            style="width: 100%;"
          >
            <el-option
              v-for="e in operationsSupervisorList"
              :key="e._id"
              :label="formatOperationsOptionLabel(e)"
              :value="e._id"
            ></el-option>
          </el-select>
        </el-form-item>
        <!-- 配送仓库选择，从静态配置列表中选择（仅省级行可配） -->
        <el-form-item v-if="!formData.city" label="配送仓库">
          <el-select v-model="formData.warehouse" placeholder="请选择仓库" filterable clearable style="width: 100%;">
            <el-option v-for="w in warehouseList" :key="w" :label="w" :value="w"></el-option>
          </el-select>
        </el-form-item>
        <!-- 起订量输入，单位为件（仅省级行可配） -->
        <el-form-item v-if="!formData.city" label="起订量(件)">
          <el-input-number v-model="formData.minOrder" :min="0" :step="1" style="width: 100%;"></el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="handleSubmit" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { provinceList, warehouseList } from '@/utility/staticData'

export default {
  name: 'SupervisorRegion',
  data () {
    return {
      // 搜索条件
      searchProvince: '',
      searchCity: '',
      searchConstructionSupervisor: '',
      searchOperationsSupervisor: '',
      searchWarehouse: '',
      // 搜索栏城市下拉选项（随省份联动加载）
      searchCityList: [],
      // 列表数据
      list: [],
      loading: false,
      // 弹窗状态
      dialogVisible: false,
      dialogMode: 'add', // add 或 edit
      submitting: false,
      // 弹窗表单数据
      formData: {
        province: '',
        city: '',
        constructionSupervisorId: '',
        constructionSupervisorName: '',
        operationsSupervisorId: '',
        operationsSupervisorName: '',
        warehouse: '',
        minOrder: 0
      },
      // 弹窗城市下拉选项（随省份联动加载）
      dialogCityList: [],
      // 城市下拉加载状态
      cityLoading: false,
      // 静态数据
      provinceList,
      warehouseList,
      // 同步营运督导加载状态
      reassigning: false,
      // 导入导出相关
      importUrl: this.$http.defaults.baseURL + 'supervisor-region/import',
      uploadHeaders: {
        authorization: window.sessionStorage.getItem('token'),
        Accept: 'application/json'
      },
      // 员工下拉列表（按角色分类），搜索栏和弹窗共用，页面加载时请求一次
      constructionSupervisorList: [],
      operationsSupervisorList: []
    }
  },
  created () {
    this.loadList()
    // 页面加载时获取督导员工列表，搜索栏和弹窗共用
    this.loadAllEmployees()
  },
  methods: {
    /**
     * 加载配置列表
     * 将当前搜索条件组装为查询参数请求后端接口
     */
    async loadList () {
      this.loading = true
      try {
        const params = {}
        if (this.searchProvince) params.province = this.searchProvince
        if (this.searchCity) params.city = this.searchCity
        if (this.searchConstructionSupervisor) params.constructionSupervisorName = this.searchConstructionSupervisor
        if (this.searchOperationsSupervisor) params.operationsSupervisorName = this.searchOperationsSupervisor
        if (this.searchWarehouse) params.warehouse = this.searchWarehouse
        const { data: res } = await this.$http.get('supervisor-region/list', { params })
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }
        this.list = res.data.list
      } catch (err) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },

    /**
     * 搜索栏省份变更：清空已选城市、联动加载城市选项并重新查询
     * 响应返回时校验省份未被再次切换，防止并发请求乱序覆盖下拉选项
     */
    async onSearchProvinceChange () {
      this.searchCity = ''
      this.searchCityList = []
      if (this.searchProvince) {
        const target = this.searchProvince
        const list = await this.loadCityOptions(target)
        if (this.searchProvince === target) {
          this.searchCityList = list
        }
      }
      this.loadList()
    },

    /**
     * 按省份加载城市下拉选项（来自后端 regions 行政区划表）
     * @param {string} province - 省份名称
     * @returns {Array} 城市列表 [{ code, name }]
     */
    async loadCityOptions (province) {
      try {
        const { data: res } = await this.$http.get('region/cities', { params: { province } })
        if (res.meta.status === 200) {
          return res.data || []
        }
        return []
      } catch (err) {
        return []
      }
    },

    /**
     * 列表内点击快速筛选
     * 点击某行中的督导或仓库，自动填入对应搜索框并清空其他搜索条件
     * @param {string} type - 筛选维度：construction/operations/warehouse
     * @param {string} value - 筛选值
     */
    quickFilter (type, value) {
      // 清空其他搜索条件，只保留当前点击的维度
      this.searchProvince = ''
      this.searchCity = ''
      this.searchCityList = []
      this.searchConstructionSupervisor = ''
      this.searchOperationsSupervisor = ''
      this.searchWarehouse = ''
      if (type === 'construction') {
        this.searchConstructionSupervisor = value
      } else if (type === 'operations') {
        this.searchOperationsSupervisor = value
      } else if (type === 'warehouse') {
        this.searchWarehouse = value
      }
      this.loadList()
    },

    /**
     * 页面加载时一次性获取所有督导员工列表
     * 搜索栏下拉和弹窗下拉共用，避免重复请求
     * 营运督导下拉同时包含营运督导与筹建督导岗位（存在一人兼两岗的情况），
     * 筹建督导岗位的人是兼职候选，选项上带岗位标注便于识别
     */
    async loadAllEmployees () {
      const [construction, operations] = await Promise.all([
        this.loadEmployees('supervisor'),
        this.loadEmployees('operatingSupervisor')
      ])
      this.constructionSupervisorList = construction.filter(e => e.job === '筹建督导')
      // 合并营运督导与筹建督导岗位，按 _id 去重（两个接口返回同一批督导部员工，合并以防将来分开）
      const merged = {}
      ;[...operations, ...construction].forEach(e => {
        merged[e._id] = e
      })
      const mergedList = Object.values(merged).filter(e => e.job === '营运督导' || e.job === '筹建督导')
      // 营运督导岗位排前、筹建督导岗位（兼职候选）排后，便于优先选到专职营运督导
      this.operationsSupervisorList = [
        ...mergedList.filter(e => e.job === '营运督导'),
        ...mergedList.filter(e => e.job !== '营运督导')
      ]
    },

    /**
     * 营运督导下拉选项显示文案：非营运督导岗位（兼职候选）带岗位标注
     * @param {Object} e - 员工对象
     * @returns {string} 显示文案
     */
    formatOperationsOptionLabel (e) {
      return e.job === '营运督导' ? e.name : `${e.name}（${e.job}）`
    },

    /**
     * 按角色加载员工列表
     * @param {string} role - 角色标识：supervisor（筹建督导）/ operatingSupervisor（营运督导）
     * @returns {Array} 员工列表
     */
    async loadEmployees (role) {
      try {
        const { data: res } = await this.$http.get('process/getEmployeeList', { params: { role } })
        if (res.meta.status === 200) {
          return res.data || []
        }
        return []
      } catch (err) {
        return []
      }
    },

    /**
     * 打开新增配置弹窗
     * 重置表单，复用已加载的员工列表，清空城市选项（待选择省份后联动加载）
     */
    openAddDialog () {
      this.dialogMode = 'add'
      this.dialogCityList = []
      this.formData = {
        province: '',
        city: '',
        constructionSupervisorId: '',
        constructionSupervisorName: '',
        operationsSupervisorId: '',
        operationsSupervisorName: '',
        warehouse: '',
        minOrder: 0
      }
      this.dialogVisible = true
    },

    /**
     * 打开编辑配置弹窗
     * 将当前行数据填入表单，复用已加载的员工列表；
     * 城市例外行需按省份加载城市选项，保证已选城市能正常回显
     * @param {Object} row - 当前行数据
     */
    async openEditDialog (row) {
      this.dialogMode = 'edit'
      this.dialogCityList = []
      this.formData = {
        province: row.province,
        city: row.city || '',
        constructionSupervisorId: row.constructionSupervisorId || '',
        constructionSupervisorName: row.constructionSupervisorName || '',
        operationsSupervisorId: row.operationsSupervisorId || '',
        operationsSupervisorName: row.operationsSupervisorName || '',
        warehouse: row.warehouse || '',
        minOrder: row.minOrder || 0
      }
      // 编辑时省份/城市不可改，但需加载城市选项以便回显
      if (row.city) {
        this.cityLoading = true
        const list = await this.loadCityOptions(row.province)
        // 响应返回时校验仍为当前编辑行，防止快速点击另一行时乱序覆盖
        if (this.dialogMode === 'edit' && this.formData.province === row.province) {
          this.dialogCityList = list
        }
        this.cityLoading = false
      }
      this.dialogVisible = true
    },

    /**
     * 弹窗省份变更：清空已选城市、联动加载该省城市选项
     * 响应返回时校验省份未被再次切换，防止并发请求乱序覆盖下拉选项
     */
    async onDialogProvinceChange () {
      this.formData.city = ''
      this.dialogCityList = []
      if (this.formData.province) {
        const target = this.formData.province
        this.cityLoading = true
        const list = await this.loadCityOptions(target)
        if (this.formData.province === target) {
          this.dialogCityList = list
        }
        this.cityLoading = false
      }
    },

    /**
     * 弹窗城市变更：选中城市时清空仅省级行有效的字段，避免隐藏字段残留提交
     * （后端对城市例外行同样强制清空，双保险）
     */
    onDialogCityChange (val) {
      if (val) {
        this.formData.constructionSupervisorId = ''
        this.formData.constructionSupervisorName = ''
        this.formData.warehouse = ''
        this.formData.minOrder = 0
      }
    },

    /**
     * 筹建督导选择变更时，同步更新姓名字段
     */
    onConstructionSupervisorChange (val) {
      const emp = this.constructionSupervisorList.find(e => e._id === val)
      this.formData.constructionSupervisorName = emp ? emp.name : ''
    },

    /**
     * 营运督导选择变更时，同步更新姓名字段
     */
    onOperationsSupervisorChange (val) {
      const emp = this.operationsSupervisorList.find(e => e._id === val)
      this.formData.operationsSupervisorName = emp ? emp.name : ''
    },

    /**
     * 提交新增或编辑表单
     */
    async handleSubmit () {
      if (!this.formData.province) return this.$message.warning('请选择省份')
      // 城市例外行必须选择营运督导，否则例外行无意义
      if (this.formData.city && !this.formData.operationsSupervisorId) {
        return this.$message.warning('城市例外行必须选择营运督导')
      }

      this.submitting = true
      try {
        const url = this.dialogMode === 'add' ? 'supervisor-region/add' : 'supervisor-region/modify'
        const { data: res } = await this.$http.post(url, this.formData)
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }
        this.$message.success(this.dialogMode === 'add' ? '添加成功' : '修改成功')
        this.dialogVisible = false
        this.loadList()
      } catch (err) {
        this.$message.error('操作失败')
      } finally {
        this.submitting = false
      }
    },

    /**
     * 删除配置，二次确认后调用删除接口
     * @param {Object} row - 要删除的行数据
     */
    async handleRemove (row) {
      const regionLabel = row.city ? `${row.province} - ${row.city}` : row.province
      try {
        await this.$confirm(`确定删除 ${regionLabel} 的配置吗？`, '提示', { type: 'warning' })
        const { data: res } = await this.$http.post('supervisor-region/remove', { province: row.province, city: row.city || '' })
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }
        this.$message.success('删除成功')
        this.loadList()
      } catch (err) {
        // 用户取消确认
      }
    },

    // ========== 导入导出相关 ==========

    /**
     * 导出当前区域配置为 Excel 文件
     * 使用 axios 请求（自动携带 Authorization header），再通过 Blob 触发下载
     */
    async handleExport () {
      try {
        const { data } = await this.$http.get('supervisor-region/export', { responseType: 'blob' })
        this._downloadBlob(data, '区域配置.xlsx')
      } catch (err) {
        this.$message.error('导出失败')
      }
    },

    /**
     * 下载导入模板
     */
    async handleDownloadTemplate () {
      try {
        const { data } = await this.$http.get('supervisor-region/template', { responseType: 'blob' })
        this._downloadBlob(data, '区域配置导入模板.xlsx')
      } catch (err) {
        this.$message.error('下载模板失败')
      }
    },

    /**
     * 通用的 Blob 文件下载方法
     * @param {Blob} blob - 文件二进制数据
     * @param {string} filename - 下载文件名
     */
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    /**
     * 导入上传前校验
     */
    beforeImportUpload (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ]
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
        this.$message.error('只能上传 xlsx 或 xls 文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },

    /**
     * 导入成功回调
     */
    handleImportSuccess (res) {
      if (res.meta && res.meta.status !== 200) {
        return this.$message.error(res.meta.msg || '导入失败')
      }
      const data = res.data
      const msg = `导入完成：共 ${data.total} 条，成功 ${data.success} 条，失败 ${data.fail} 条`
      if (data.details && data.details.length > 0) {
        this.$notify({
          title: '导入结果',
          message: msg + '\n' + data.details.join('\n'),
          type: data.fail > 0 ? 'warning' : 'success',
          duration: 8000
        })
      } else {
        this.$message.success(msg)
      }
      // 刷新列表
      this.loadList()
    },

    /**
     * 导入失败回调
     */
    handleImportError () {
      this.$message.error('导入失败，请检查文件格式')
    },

    // ========== 同步营运督导相关 ==========

    /**
     * 按最新区域配置同步所有品牌门店的营运督导
     * 二次确认后调用后端 reassign 接口，用 $notify 展示详细统计
     */
    async handleReassign () {
      try {
        await this.$confirm(
          '将按当前区域配置，批量更新所有品牌项目门店（待运营/运营中）的营运督导。是否继续？',
          '同步到门店',
          { type: 'warning', confirmButtonText: '开始同步', cancelButtonText: '取消' }
        )
      } catch (e) {
        return // 用户取消
      }

      this.reassigning = true
      try {
        const { data: res } = await this.$http.post('supervisor-region/reassign', {}, { timeout: 60000 })
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }
        const d = res.data
        const msg = `同步完成：扫描 ${d.total} 家门店，更新 ${d.updated} 家，未变 ${d.unchanged} 家，跳过 ${d.skipped} 家`
        // 有跳过明细时用 $notify 展示详情，否则用 $message
        if (d.details && d.details.length > 0) {
          this.$notify({
            title: '同步到门店结果',
            message: msg + '\n' + d.details.slice(0, 20).join('\n') + (d.details.length > 20 ? `\n...等共 ${d.details.length} 条` : ''),
            type: d.skipped > 0 ? 'warning' : 'success',
            duration: 10000
          })
        } else {
          this.$message.success(msg)
        }
      } catch (err) {
        this.$message.error('同步失败')
      } finally {
        this.reassigning = false
      }
    }
  }
}
</script>

<style scoped>
/* 可点击链接样式，用于列表中的督导和仓库列 */
.link-text {
  color: #409eff;
  cursor: pointer;
}
.link-text:hover {
  text-decoration: underline;
}
/* 未配置提示文字样式 */
.text-muted {
  color: #909399;
}
</style>
